import React, { useState } from 'react'
import { Eye, EyeOff, Lock, Mail, User } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { Input } from '../ui/Input'
import { Dialog, DialogContent, DialogTitle } from '../ui/Dialog'
import './login-modal.css'

type AuthMode = 'login' | 'register'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MIN_PASSWORD_LENGTH = 8 // Must match backend (auth-schemas.ts)

const VALIDATION_MESSAGES = {
	INVALID_EMAIL: 'Please enter a valid email address',
	PASSWORD_TOO_SHORT: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
	PASSWORD_NO_LOWERCASE: 'Password must contain at least one lowercase letter',
	PASSWORD_NO_UPPERCASE: 'Password must contain at least one uppercase letter',
	PASSWORD_NO_NUMBER: 'Password must contain at least one number',
	NAME_REQUIRED: 'Name is required',
} as const

type FormData = {
	email: string
	password: string
	name: string
}

type FormState = {
	data: FormData
	showPassword: boolean
	isSubmitting: boolean
}

type ErrorState = {
	validation: string
	submission: string
}

type LoginModalProps = {
	open?: boolean
	onOpenChange?: (open: boolean) => void
	onSuccess?: () => void
}

function validateEmail(email: string): boolean {
	return EMAIL_REGEX.test(email)
}

/**
 * Validates password meets complexity requirements matching backend AuthSchemas.
 * Returns error message or null if valid.
 */
function validatePassword(password: string): string | null {
	if (password.length < MIN_PASSWORD_LENGTH) {
		return VALIDATION_MESSAGES.PASSWORD_TOO_SHORT
	}
	if (!/[a-z]/.test(password)) {
		return VALIDATION_MESSAGES.PASSWORD_NO_LOWERCASE
	}
	if (!/[A-Z]/.test(password)) {
		return VALIDATION_MESSAGES.PASSWORD_NO_UPPERCASE
	}
	if (!/[0-9]/.test(password)) {
		return VALIDATION_MESSAGES.PASSWORD_NO_NUMBER
	}
	return null
}

function validateRegistrationForm(formData: FormData): string | null {
	if (!validateEmail(formData.email)) {
		return VALIDATION_MESSAGES.INVALID_EMAIL
	}
	const passwordError = validatePassword(formData.password)
	if (passwordError) {
		return passwordError
	}
	if (!formData.name.trim()) {
		return VALIDATION_MESSAGES.NAME_REQUIRED
	}
	return null
}

export function LoginModal({ open = true, onOpenChange, onSuccess }: LoginModalProps = {}) {
	const { login, register } = useAuth()
	const [mode, setMode] = useState<AuthMode>('login')

	const [formState, setFormState] = useState<FormState>({
		data: { email: '', password: '', name: '' },
		showPassword: false,
		isSubmitting: false,
	})

	const [errors, setErrors] = useState<ErrorState>({
		validation: '',
		submission: '',
	})

	const updateFormData = (field: keyof FormData, value: string) => {
		setFormState(prev => ({
			...prev,
			data: { ...prev.data, [field]: value },
		}))
	}

	const clearErrors = () => {
		setErrors({ validation: '', submission: '' })
	}

	const handleValidation = (): boolean => {
		clearErrors()

		if (mode === 'register') {
			const validationError = validateRegistrationForm(formState.data)
			if (validationError) {
				setErrors(prev => ({ ...prev, validation: validationError }))
				return false
			}
		}

		return true
	}

	const handleSubmit = async (e: React.FormEvent): Promise<void> => {
		e.preventDefault()

		if (!handleValidation()) {
			return
		}

		setFormState(prev => ({ ...prev, isSubmitting: true }))

		try {
			const { email, password, name } = formState.data

			if (mode === 'login') {
				await login({ email, password })
			} else {
				await register({ email, name, password })
			}

			onSuccess?.()
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'An error occurred'
			setErrors(prev => ({ ...prev, submission: errorMessage }))
		} finally {
			setFormState(prev => ({ ...prev, isSubmitting: false }))
		}
	}

	const switchMode = (): void => {
		setMode(prev => prev === 'login' ? 'register' : 'login')
		clearErrors()
		// Clear form data on mode switch to prevent state leakage
		setFormState(prev => ({
			...prev,
			data: { email: '', password: '', name: '' },
		}))
	}

	const togglePasswordVisibility = () => {
		setFormState(prev => ({ ...prev, showPassword: !prev.showPassword }))
	}

	const isLogin = mode === 'login'
	const displayError = errors.validation || errors.submission
	const { data, showPassword, isSubmitting } = formState

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="login-modal" hideClose={!onOpenChange} aria-describedby={undefined}>
				<DialogTitle className="sr-only">
					{isLogin ? 'Sign In' : 'Create Account'}
				</DialogTitle>

				<div className="login-modal-header">
					<h2 className="login-modal-title">
						{isLogin ? 'Sign in' : 'Create account'}
					</h2>
					<p className="login-modal-subtitle">
						{isLogin
							? 'Enter your credentials to continue'
							: 'Get started with PlaySmith'}
					</p>
				</div>

				<form onSubmit={handleSubmit} className="login-modal-form">
					{displayError && (
						<div className="login-modal-error">
							{displayError}
						</div>
					)}

					{!isLogin && (
						<div className="login-modal-form-group">
							<label htmlFor="name" className="login-modal-label">
								Name
							</label>
							<div className="login-modal-input-wrapper">
								<User className="login-modal-input-icon" />
								<Input
									id="name"
									type="text"
									value={data.name}
									onChange={e => updateFormData('name', e.target.value)}
									placeholder="Your name"
									required={!isLogin}
								/>
							</div>
						</div>
					)}

					<div className="login-modal-form-group">
						<label htmlFor="email" className="login-modal-label">
							Email
						</label>
						<div className="login-modal-input-wrapper">
							<Mail className="login-modal-input-icon" />
							<Input
								id="email"
								type="email"
								value={data.email}
								onChange={e => updateFormData('email', e.target.value)}
								placeholder="coach@example.com"
								required
							/>
						</div>
					</div>

					<div className="login-modal-form-group" data-spacing="large">
						<label htmlFor="password" className="login-modal-label">
							Password
						</label>
						<div className="login-modal-input-wrapper has-toggle">
							<Lock className="login-modal-input-icon" />
							<Input
								id="password"
								type={showPassword ? 'text' : 'password'}
								value={data.password}
								onChange={e => updateFormData('password', e.target.value)}
								placeholder="Enter your password"
								required
							/>
							<button
								type="button"
								onClick={togglePasswordVisibility}
								className="login-modal-password-toggle"
								data-active={showPassword}
								aria-label="Toggle password visibility"
							>
								{showPassword ? <EyeOff /> : <Eye />}
							</button>
						</div>
					</div>

					<button
						type="submit"
						disabled={isSubmitting}
						className="login-modal-submit"
					>
						{isSubmitting
							? 'Please wait...'
							: isLogin ? 'Sign In' : 'Create Account'
						}
					</button>

					<div className="login-modal-divider">
						<span className="login-modal-divider-text">or</span>
					</div>

					<p className="login-modal-switch">
						{isLogin
							? 'Don\'t have an account? '
							: 'Already have an account? '
						}
						<button
							type="button"
							onClick={switchMode}
							className="login-modal-switch-button"
						>
							{isLogin ? 'Sign up' : 'Sign in'}
						</button>
					</p>
				</form>
			</DialogContent>
		</Dialog>
	)
}
